import React, { useRef } from 'react';
import { Button, Col, Row } from 'react-bootstrap';

import { useProfile, useScrollPosition } from '../../../hooks';
import { subscriber_profile_path } from '../../../routes';
import { Spinner } from '../../BaseUI';
import Directory from './Directory';
import Skills from './Entities/Skills';
import Topics from './Entities/Topics';
import Weekend from './Entities/Weekend';
import OpenTo from './OpenTo';
import Profile from './Profile';
import ProfileImage from './ProfileImage';
import SideNav from './SideNav';
import Social from './Social';

interface UserProfileFormProps {
  setShowEnableProfileBanner: (show: boolean) => void;
}

const UserProfileForm = ({ setShowEnableProfileBanner }: UserProfileFormProps) => {
  const { profile, isLoadingProfile } = useProfile();
  const profileStartRef = useRef(null);

  useScrollPosition(
    ({ currPos }) => {
      // show banner to enable profile is it's not enabled and user scrolls past top
      // profile section to the form inputs
      const showBanner =
        profileStartRef.current?.offsetHeight &&
        profileStartRef.current?.offsetHeight < Math.abs(currPos.y) &&
        profile &&
        !profile.profileEnabled;
      setShowEnableProfileBanner(showBanner);
    },
    [profile?.profileEnabled, profileStartRef.current?.offsetHeight]
  );

  return (
    <>
      {isLoadingProfile ? (
        <Spinner />
      ) : (
        <>
          <Row className="profile-start" ref={profileStartRef}>
            <Col className="col-sm-12 col-md-8 profile-wrapper">
              <div className="profile-image-wrapper">
                <div className="profile-header-detail">
                  <ProfileImage userId={profile.id} profileImage={profile.avatarUrl} />
                  <div className="profile-header">
                    <div className="name-container">{profile.name}</div>
                  </div>
                </div>
              </div>
            </Col>
            <Col className="col-sm-12 col-md-4 contact-wrapper">
              <div className="contact-container">
                <Button
                  className="profile-button --white"
                  onClick={() => (window.location = subscriber_profile_path(profile.username))}
                >
                  View My Profile
                </Button>
              </div>
            </Col>
          </Row>
          <Row className="settings-section-wrapper">
            <SideNav />
            <Col className="col-sm-12 col-md-10 profile-settings-wrapper">
              <Directory
                isLoading={isLoadingProfile}
                userId={profile.id}
                profileEnabled={profile.profileEnabled}
                messageEnabled={profile.messageEnabled}
                mentioningDisabled={profile.mentioningDisabled}
                messagingBlocked={profile.messagingBlocked}
              />
              <Profile
                isDisabled={!profile.profileEnabled}
                userId={profile.id}
                oneLineBio={profile.oneLineBio}
                username={profile.username}
                company={profile.company}
                location={profile.location}
                title={profile.titleAtCompany}
                bio={profile.bio}
                shortBio={profile.shortBio}
                isStaff={profile.isStaff}
              />
              <OpenTo
                isDisabled={!profile.profileEnabled}
                userId={profile.id}
                investingInStartups={profile.investingInStartups}
                investingInStartupsDesc={profile.investingInStartupsDesc}
                hiring={profile.hiring}
                hiringDesc={profile.hiringDesc}
                findingNewJob={profile.findingNewJob}
                findingNewJobDesc={profile.findingNewJobDesc}
                providingServices={profile.providingServices}
                providingServicesDesc={profile.providingServicesDesc}
              />
              <Social
                isDisabled={!profile.profileEnabled}
                userId={profile.id}
                twitter={profile.twitter}
                linkedin={profile.linkedin}
                instagram={profile.instagram}
                personalUrl={profile.personalUrl}
              />
              <Skills
                userId={profile.id}
                skills={profile.skills}
                isDisabled={!profile.profileEnabled}
              />
              <Topics
                userId={profile.id}
                topics={profile.topics}
                isDisabled={!profile.profileEnabled}
              />
              <Weekend
                userId={profile.id}
                isDisabled={!profile.profileEnabled}
                favoriteVacationSpots={profile.favoriteVacationSpots}
                favoriteSportsToPlay={profile.favoriteSportsToPlay}
                favoriteSportsToWatch={profile.favoriteSportsToWatch}
                favoriteBooks={profile.favoriteBooks}
                favoriteShows={profile.favoriteShows}
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default UserProfileForm;
